import { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { UserCircle, IdentificationBadge, Lock, Envelope, Phone, User, Eye, EyeSlash, Sparkle } from "@phosphor-icons/react";

const BRAND = "The WorkGS: SDM";
const TAGLINE = "Orchestrating Excellence in Service Delivery.";
const LOGO = "/kgs-logo-horizontal.png";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Format any FastAPI detail (string / list / dict) into a safe string. */
function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return formatApiError(detail);
}

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const initialSide = (search.get("tab") || "").toLowerCase() === "candidate" ? "candidate" : "staff";
  const [side, setSide] = useState(initialSide);

  // Portal token → if present on the candidate tab, bounce to the portal.
  // Hooks MUST run in the same order on every render, so this useEffect stays
  // above the conditional early return below.
  useEffect(() => {
    if (side === "candidate" && sessionStorage.getItem("portal_token")) {
      nav("/candidate-portal", { replace: true });
    }
  }, [side, nav]);

  // If a staff user is already logged in, bounce back to the app root.
  if (user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="unified-login-page">
      {/* Brand bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="KGS" className="h-9 w-auto" />
            <div>
              <div className="font-semibold text-slate-900 tracking-tight text-base leading-tight">{BRAND}</div>
              <div className="hidden sm:block text-xs text-slate-500 leading-tight">Service Delivery Management</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-500">
            <Sparkle size={12} className="text-amber-500" /> {TAGLINE}
          </div>
        </div>
      </header>

      <main className="flex-1 grid lg:grid-cols-2 max-w-6xl w-full mx-auto px-4 sm:px-6 py-8 gap-8 items-start">
        {/* LEFT — Brand pane */}
        <section className="hidden lg:flex flex-col justify-between h-full bg-slate-900 text-white rounded-2xl p-10">
          <div>
            <img src={LOGO} alt="KGS" className="h-12 w-auto brightness-0 invert opacity-90 mb-8" />
            <h1 className="text-4xl font-semibold leading-tight tracking-tight">{BRAND}</h1>
            <p className="text-slate-300 text-sm mt-3 flex items-center gap-1.5">
              <Sparkle size={14} className="text-amber-400" /> {TAGLINE}
            </p>
            <p className="text-slate-300 text-base leading-relaxed mt-8 max-w-md">
              One unified platform for candidates, staff and delivery leaders — real-time
              service tracking, evaluations, interviews, offers and performance analytics.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-8 text-sm text-slate-300 max-w-md">
            {["Role-based dashboards", "Approvals & audit trail", "Candidate self service", "Real-time reports"].map((f) => (
              <div key={f} className="flex items-center gap-2 border border-slate-700 rounded-md px-3 py-2">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400" /> {f}
              </div>
            ))}
          </div>
        </section>

        {/* RIGHT — Login card */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
          <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">Sign in to {BRAND}</h2>
          <p className="text-sm text-slate-500 mt-1">Choose how you're joining today.</p>

          {/* Segmented control — Staff vs Candidate */}
          <div className="mt-5 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl" role="tablist">
            <SideTab active={side === "staff"} onClick={() => setSide("staff")}
                     testid="login-tab-staff" Icon={IdentificationBadge} label="Staff Login" />
            <SideTab active={side === "candidate"} onClick={() => setSide("candidate")}
                     testid="login-tab-candidate" Icon={UserCircle} label="Candidate Login" />
          </div>

          <div className="mt-6">
            {side === "staff" ? <StaffLoginForm onLogin={login} nav={nav} /> : <CandidatePanel nav={nav} />}
          </div>
        </section>
      </main>

      <footer className="text-center text-xs text-slate-400 py-6">
        © {new Date().getFullYear()} KGS Technology Group · {BRAND} · {TAGLINE}
      </footer>
    </div>
  );
}

function SideTab({ active, onClick, Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition
                        ${active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-800"}`}
            aria-pressed={active}>
      <Icon size={16} weight={active ? "fill" : "regular"} />
      {label}
    </button>
  );
}

/* -------- Staff Login (unchanged workflow — POST /api/auth/login) -------- */
function StaffLoginForm({ onLogin, nav }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setErr("Enter your email address.");
    if (!password) return setErr("Enter your password.");
    setBusy(true); setErr("");
    try {
      await onLogin(email, password);
      toast.success(`Welcome back!`);
      nav("/", { replace: true });
    } catch (ex) {
      const msg = formatDetail(ex.response?.data?.detail) || ex.message;
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="staff-login-form" className="space-y-4">
      <Field label="Work email">
        <input type="email" required autoFocus value={email}
               onChange={(e) => setEmail(e.target.value)}
               data-testid="staff-email-input"
               placeholder="you@company.com" className={INPUT_CLASS} />
        <Envelope size={16} className={ICON_CLASS} />
      </Field>
      <Field label="Password">
        <input type={show ? "text" : "password"} required value={password}
               onChange={(e) => setPassword(e.target.value)}
               data-testid="staff-password-input"
               placeholder="••••••••" className={INPUT_CLASS} />
        <Lock size={16} className={ICON_CLASS} />
        <EyeToggle show={show} setShow={setShow} testid="staff-toggle-visibility" />
      </Field>
      {err && <p className="text-sm text-red-600" data-testid="staff-login-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="staff-login-submit" className={BTN_PRIMARY}>
        {busy ? "Signing in…" : "Sign in to Staff Portal"}
      </button>
      <p className="text-xs text-slate-500 text-center">
        Your role, dashboards and permissions remain unchanged.
      </p>
    </form>
  );
}

/* -------- Candidate side — Sign in OR Create Account -------- */
function CandidatePanel({ nav }) {
  const [mode, setMode] = useState("signin");
  return (
    <div data-testid="candidate-panel">
      <div className="flex items-center gap-2 mb-4 text-xs">
        <button onClick={() => setMode("signin")} data-testid="candidate-mode-signin"
                className={`px-3 py-1.5 rounded-full font-medium transition
                            ${mode === "signin" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>
          Sign in
        </button>
        <button onClick={() => setMode("signup")} data-testid="candidate-mode-signup"
                className={`px-3 py-1.5 rounded-full font-medium transition
                            ${mode === "signup" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>
          Create Candidate Account
        </button>
      </div>
      {mode === "signin"
        ? <CandidateSignInForm nav={nav} onSwitch={() => setMode("signup")} />
        : <CandidateSignUpForm nav={nav} onSwitch={() => setMode("signin")} />}
    </div>
  );
}

function CandidateSignInForm({ nav, onSwitch }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setErr("Enter your registered email address.");
    if (!password) return setErr("Enter your password.");
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${BACKEND}/api/portal/login`, { email, password });
      sessionStorage.setItem("portal_token", data.access_token);
      toast.success("Welcome back!");
      nav("/candidate-portal", { replace: true });
    } catch (ex) {
      const msg = formatDetail(ex.response?.data?.detail) || ex.message;
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="candidate-signin-form" className="space-y-4">
      <Field label="Registered email">
        <input type="email" required autoFocus value={email}
               onChange={(e) => setEmail(e.target.value)}
               data-testid="candidate-signin-email"
               placeholder="you@example.com" className={INPUT_CLASS} />
        <Envelope size={16} className={ICON_CLASS} />
      </Field>
      <Field label="Password">
        <input type={show ? "text" : "password"} required value={password}
               onChange={(e) => setPassword(e.target.value)}
               data-testid="candidate-signin-password"
               placeholder="••••••••" className={INPUT_CLASS} />
        <Lock size={16} className={ICON_CLASS} />
        <EyeToggle show={show} setShow={setShow} testid="candidate-signin-toggle" />
      </Field>
      {err && <p className="text-sm text-red-600" data-testid="candidate-signin-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="candidate-signin-submit" className={BTN_PRIMARY}>
        {busy ? "Signing in…" : "Sign in to Candidate Portal"}
      </button>
      <p className="text-xs text-slate-500 text-center">
        New to {BRAND}?{" "}
        <button type="button" onClick={onSwitch}
                data-testid="candidate-goto-signup"
                className="text-sky-600 hover:text-sky-700 font-medium">
          Create your Candidate Account
        </button>
      </p>
    </form>
  );
}

function CandidateSignUpForm({ nav, onSwitch }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "",
                                     mobile: "", password: "", confirm_password: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [route, setRoute] = useState(null); // "candidate_login" prompts a switch

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setRoute(null);
    if (!form.first_name || !form.last_name) return setErr("Enter your first and last name.");
    if (!form.email.trim()) return setErr("Enter a valid email address.");
    if (!form.mobile.trim()) return setErr("Enter your mobile number.");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (form.password !== form.confirm_password) return setErr("Password and Confirm Password do not match.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/public/signup`, form);
      sessionStorage.setItem("portal_token", data.access_token);
      toast.success("Welcome! Let's complete your dossier.");
      nav("/candidate-portal", { replace: true });
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.route) {
        setRoute(detail.route);
        setErr(detail.message || "You already have a registration on file.");
      } else {
        setErr(formatDetail(detail) || ex.message);
      }
      toast.error(typeof detail === "object" ? detail.message : formatDetail(detail));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="candidate-signup-form" className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <input required value={form.first_name} onChange={set("first_name")}
                 data-testid="signup-first-name" placeholder="Alex" className={INPUT_CLASS} />
          <User size={16} className={ICON_CLASS} />
        </Field>
        <Field label="Last name">
          <input required value={form.last_name} onChange={set("last_name")}
                 data-testid="signup-last-name" placeholder="Kim" className={INPUT_CLASS} />
          <User size={16} className={ICON_CLASS} />
        </Field>
      </div>
      <Field label="Email address">
        <input type="email" required value={form.email} onChange={set("email")}
               data-testid="signup-email" placeholder="you@example.com" className={INPUT_CLASS} />
        <Envelope size={16} className={ICON_CLASS} />
      </Field>
      <Field label="Mobile number">
        <input type="tel" required value={form.mobile} onChange={set("mobile")}
               data-testid="signup-mobile"
               placeholder="+1 555 123 4567" className={INPUT_CLASS} />
        <Phone size={16} className={ICON_CLASS} />
      </Field>
      <Field label="Create password (min 8 characters)">
        <input type={show ? "text" : "password"} required value={form.password}
               onChange={set("password")} data-testid="signup-password"
               placeholder="At least 8 characters" className={INPUT_CLASS} />
        <Lock size={16} className={ICON_CLASS} />
        <EyeToggle show={show} setShow={setShow} testid="signup-toggle-visibility" />
      </Field>
      <Field label="Confirm password">
        <input type={show ? "text" : "password"} required value={form.confirm_password}
               onChange={set("confirm_password")} data-testid="signup-confirm-password"
               placeholder="Retype your password" className={INPUT_CLASS} />
        <Lock size={16} className={ICON_CLASS} />
      </Field>
      {err && (
        <div className="text-sm text-red-600 space-y-1" data-testid="candidate-signup-error">
          <p>{err}</p>
          {route === "candidate_login" && (
            <button type="button" onClick={onSwitch}
                    data-testid="signup-goto-login"
                    className="text-sky-600 hover:text-sky-700 font-medium">
              Go to Candidate Login →
            </button>
          )}
        </div>
      )}
      <button type="submit" disabled={busy} data-testid="candidate-signup-submit" className={BTN_PRIMARY}>
        {busy ? "Creating your account…" : "Create Candidate Account"}
      </button>
      <p className="text-xs text-slate-500 text-center">
        By creating an account you consent to your dossier being reviewed by our delivery team.
      </p>
    </form>
  );
}

/* -------- Small building blocks -------- */
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">{label}</span>
      <div className="relative mt-1">{children}</div>
    </label>
  );
}
function EyeToggle({ show, setShow, testid }) {
  return (
    <button type="button" onClick={() => setShow((v) => !v)}
            data-testid={testid}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600">
      {show ? <EyeSlash size={16} /> : <Eye size={16} />}
    </button>
  );
}
const INPUT_CLASS = "w-full border border-slate-200 rounded-lg pl-9 pr-9 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500/70 transition";
const ICON_CLASS  = "absolute left-3 top-3 text-slate-400 pointer-events-none";
const BTN_PRIMARY = "w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-70 text-white text-sm font-medium py-2.5 transition";
